import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChatIcon from "@mui/icons-material/Chat";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RouteIcon from "@mui/icons-material/Route";
import { assignTaskDriver, getTasks, listDrivers, logoutDriver, removeDriverFromShift } from "../../api/client";
import { getDispatchSuggestions } from "../../api/aiClient";
import AiChatBox from "../../components/AiChatBox";
import AiSuggestionCard from "../../components/AiSuggestionCard";
import { getCurrentRole } from "../../data/users";
import {
  driverHealthFlag,
  formatAbsoluteTime,
  getAssignedTasksForDriver,
  getCurrentTaskForDriver,
  getTaskRisk,
  hasAssignedDriver,
  isDriverInTransit,
  isReassignmentRequired,
  isOnShift,
  reassignmentMessage,
  isStaleDriver,
  nextDestination,
  relativeTime,
  staleSeverity,
  taskIdentity,
} from "../../utils/dispatchOps";
import { isTerminalTaskStatus } from "../../constants/operations";

const FILTERS = {
  ALL: "all",
  ON_SHIFT: "on-shift",
  AVAILABLE: "available",
  IN_TRANSIT: "in-transit",
  OFFLINE: "offline",
  STALE: "stale",
  ON_TASK: "on-task",
};

const STATUS_BADGE = {
  available: "success",
  assigned: "info",
  "en route": "warning",
  "at pickup": "warning",
  "in transit": "warning",
  arrived: "secondary",
  delivered: "success",
  offline: "default",
};

function badgeColor(status) {
  return STATUS_BADGE[String(status || "").toLowerCase()] ?? "secondary";
}

function FilterCard({ label, value, caption, selected, onClick, color = "primary" }) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        cursor: "pointer",
        borderRadius: 3,
        height: "100%",
        border: (theme) => `1px solid ${selected ? theme.palette[color]?.main || theme.palette.primary.main : theme.palette.divider}`,
        bgcolor: selected ? `${color}.50` : "background.paper",
      }}
    >
      <CardContent sx={{ pb: "16px !important" }}>
        <Typography variant="caption" color={selected ? `${color}.main` : "text.secondary"} sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </CardContent>
    </Card>
  );
}

function HealthChip({ value }) {
  const colorMap = {
    Healthy: "success",
    Stale: "warning",
    "No task": "default",
    Blocked: "error",
    "Override pending": "secondary",
  };
  return <Chip size="small" label={value} color={colorMap[value] || "default"} variant="filled" sx={{ fontWeight: 700 }} />;
}

function QuickAssignDialog({ row, tasks, onClose, onAssign }) {
  const [taskId, setTaskId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setTaskId("");
    setSaving(false);
    setError("");
  }, [row?.driver?.id]);

  if (!row) return null;

  const availableTasks = tasks
    .filter((task) => !isTerminalTaskStatus(task.status) && !hasAssignedDriver(task))
    .sort((a, b) => getTaskRisk(a).sortWeight - getTaskRisk(b).sortWeight);

  const handleAssign = async () => {
    if (!taskId) {
      setError("Select a task to assign.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onAssign(row, taskId);
    } catch (err) {
      setError(err?.message || "Assignment failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!row} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
        <Typography fontWeight={700} fontSize={16}>
          Assign Work — {row.driver.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Quick assignment from the fleet board. Full job control remains in Task List / Courier Dashboard.
        </Typography>
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 10, minWidth: 0 }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        <TextField
          select
          fullWidth
          label="Unassigned task"
          value={taskId}
          onChange={(e) => setTaskId(e.target.value)}
          helperText={availableTasks.length ? "Only unassigned active tasks are listed." : "No unassigned active tasks are available."}
        >
          {availableTasks.map((task) => {
            const identity = taskIdentity(task);
            const risk = getTaskRisk(task);
            return (
              <MenuItem key={task.id} value={task.id}>
                {identity.title} • {identity.route} • {risk.label}
              </MenuItem>
            );
          })}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAssign} disabled={saving || !availableTasks.length}>
          {saving ? <CircularProgress size={16} color="inherit" /> : "Assign Work"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveFromShiftDialog({ row, tasks, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setNote("");
    setSaving(false);
    setError("");
  }, [row?.driver?.id]);

  if (!row) return null;

  const attachedTasks = getAssignedTasksForDriver(row.driver, tasks);

  const handleConfirm = async () => {
    setSaving(true);
    setError("");
    try {
      await onConfirm(row, note.trim(), attachedTasks);
    } catch (err) {
      setError(err?.message || "Remove From Shift failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!row} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
        <Typography fontWeight={700} fontSize={16}>
          Remove From Shift
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.driver.name}
        </Typography>
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 10, minWidth: 0 }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        <Alert severity={attachedTasks.length ? "warning" : "info"} sx={{ mb: 2 }}>
          {attachedTasks.length
            ? `${attachedTasks.length} active or pending task${attachedTasks.length === 1 ? "" : "s"} will be elevated to DRIVER REMOVED — REASSIGN IMMEDIATELY.`
            : "No active or pending tasks are attached. The driver will still be removed from active shift / operations."}
        </Alert>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
          Driver
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {row.driver.name} • {row.driver.email}
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
          Attached work
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {attachedTasks.length ? "Yes" : "No"} • {attachedTasks.length} affected task{attachedTasks.length === 1 ? "" : "s"}
        </Typography>
        {attachedTasks.length > 0 && (
          <Box sx={{ mb: 2, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 1.5, maxHeight: 180, overflowY: "auto" }}>
            <Stack spacing={1}>
              {attachedTasks.slice(0, 6).map((task) => (
                <Box key={task.id}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {taskIdentity(task).title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {task.status || "Assigned"} • {taskIdentity(task).route}
                  </Typography>
                </Box>
              ))}
              {attachedTasks.length > 6 && (
                <Typography variant="caption" color="text.secondary">
                  +{attachedTasks.length - 6} more affected tasks
                </Typography>
              )}
            </Stack>
          </Box>
        )}
        <TextField
          label="Admin note / reason (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          inputProps={{ maxLength: 500 }}
          placeholder="Vehicle breakdown, medical issue, device failure, unavailable mid-shift..."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={saving}>
          {saving ? <CircularProgress size={16} color="inherit" /> : "Confirm Removal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DriverStatus() {
  const navigate = useNavigate();
  const hasLoadedRef = useRef(false);
  const currentRole = getCurrentRole();
  const isAdmin = currentRole === "admin";
  const canLogoutDriver = currentRole === "admin" || currentRole === "dispatcher";
  // AI assistant is admin/dispatcher only (enforced server-side as well).
  const canUseAi = currentRole === "admin" || currentRole === "dispatcher";
  const [drivers, setDrivers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState({ open: false, severity: "success", message: "" });
  const [lastSync, setLastSync] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [assignRow, setAssignRow] = useState(null);
  const [removeRow, setRemoveRow] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState("");
  const [activeFilter, setActiveFilter] = useState(FILTERS.ALL);

  const pushNotice = useCallback((severity, message) => {
    setNotice({ open: true, severity, message });
  }, []);

  const closeNotice = useCallback(() => {
    setNotice((prev) => ({ ...prev, open: false }));
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    if (background || hasLoadedRef.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const [dRes, tRes] = await Promise.all([listDrivers(), getTasks()]);
      if (!dRes.ok) throw new Error(`Drivers fetch failed: ${dRes.status}`);
      if (!tRes.ok) throw new Error(`Tasks fetch failed: ${tRes.status}`);
      const dData = await dRes.json();
      const tData = await tRes.json();
      setDrivers(Array.isArray(dData) ? dData : []);
      setTasks(Array.isArray(tData) ? tData : []);
      setLastSync(new Date().toISOString());
      hasLoadedRef.current = true;
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setNowTick(Date.now());
    }
  }, []);

  useEffect(() => {
    load();
    const pollId = setInterval(() => load({ background: true }), 15_000);
    const tickId = setInterval(() => setNowTick(Date.now()), 10_000);
    return () => {
      clearInterval(pollId);
      clearInterval(tickId);
    };
  }, [load]);

  const driverRows = useMemo(() => {
    return drivers.map((driver) => {
      const currentTask = getCurrentTaskForDriver(driver, tasks);
      const onShift = isOnShift(driver);
      const stale = isStaleDriver(driver.updated_at, 5, nowTick);
      const staleLevel = staleSeverity(driver.updated_at, nowTick);
      const available = onShift && String(driver.operational_status || "").toLowerCase() === "available" && !currentTask;
      const inTransit = isDriverInTransit(driver, currentTask);
      const health = driverHealthFlag(driver, currentTask, nowTick);
      const shiftState = onShift ? "On Shift" : "No Active Shift";
      const _ua = driver.updated_at ? (String(driver.updated_at).endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(String(driver.updated_at)) ? driver.updated_at : driver.updated_at + "Z") : null;
      const idleTooLong = available && !!_ua && (nowTick - new Date(_ua).getTime()) > 20 * 60 * 1000;
      return {
        driver,
        currentTask,
        onShift,
        stale,
        staleLevel,
        available,
        inTransit,
        health,
        shiftState,
        idleTooLong,
      };
    });
  }, [drivers, tasks, nowTick]);

  const kpis = useMemo(() => {
    const onShift = driverRows.filter((row) => row.onShift);
    const available = driverRows.filter((row) => row.available);
    const inTransit = driverRows.filter((row) => row.inTransit);
    const offline = driverRows.filter((row) => !row.onShift || String(row.driver.operational_status || "").toLowerCase() === "offline");
    const stale = driverRows.filter((row) => row.stale);
    const onTask = driverRows.filter((row) => !!row.currentTask);
    return { onShift, available, inTransit, offline, stale, onTask };
  }, [driverRows]);

  const filteredRows = useMemo(() => {
    switch (activeFilter) {
      case FILTERS.ON_SHIFT:
        return driverRows.filter((row) => row.onShift);
      case FILTERS.AVAILABLE:
        return driverRows.filter((row) => row.available);
      case FILTERS.IN_TRANSIT:
        return driverRows.filter((row) => row.inTransit);
      case FILTERS.OFFLINE:
        return driverRows.filter((row) => !row.onShift || String(row.driver.operational_status || "").toLowerCase() === "offline");
      case FILTERS.STALE:
        return driverRows.filter((row) => row.stale);
      case FILTERS.ON_TASK:
        return driverRows.filter((row) => !!row.currentTask);
      default:
        return driverRows;
    }
  }, [activeFilter, driverRows]);

  const attentionRows = useMemo(() => {
    return driverRows.filter((row) => row.stale || row.idleTooLong || row.health === "Blocked" || row.health === "Override pending" || !row.driver.updated_at);
  }, [driverRows]);

  const availablePool = useMemo(() => {
    return driverRows.filter((row) => row.available && row.health !== "Stale").sort((a, b) => {
      const aTime = new Date(a.driver.updated_at || 0).getTime();
      const bTime = new Date(b.driver.updated_at || 0).getTime();
      return bTime - aTime;
    });
  }, [driverRows]);

  const assignableTasks = useMemo(
    () => tasks.filter((task) => !isTerminalTaskStatus(task.status) && !hasAssignedDriver(task)),
    [tasks]
  );

  const openMap = (row) => {
    try {
      if (row.currentTask?.id) window.sessionStorage.setItem("ci_live_map_focus_task_id", row.currentTask.id);
      if (row.driver?.id) window.sessionStorage.setItem("ci_live_map_focus_driver_id", row.driver.id);
    } catch {
      // ignore storage failures
    }
    navigate("/live-map");
  };

  const openTask = (row) => {
    try {
      if (row.currentTask?.id) window.sessionStorage.setItem("ci_focus_task_id", row.currentTask.id);
    } catch {
      // ignore storage failures
    }
    navigate("/applications/task-list");
  };

  const openRoute = (row) => {
    try {
      if (row.currentTask?.id) window.sessionStorage.setItem("ci_route_planner_focus_task_id", row.currentTask.id);
    } catch {
      // ignore storage failures
    }
    navigate("/applications/route-planner");
  };

  const handleQuickAssign = async (row, taskId) => {
    const task = tasks.find((entry) => entry.id === taskId);
    if (!task) throw new Error("Selected task was not found.");

    const nowIso = new Date().toISOString();
    const previousTasks = tasks;
    const previousDrivers = drivers;
    const optimisticTask = {
      ...task,
      status: "Assigned",
      assigned_driver_id: row.driver.id,
      driver: row.driver.name,
      assignedDriver: row.driver.name,
      updatedAt: nowIso,
    };

    setTasks((prev) => prev.map((entry) => (entry.id === taskId ? optimisticTask : entry)));
    setDrivers((prev) =>
      prev.map((driver) =>
        driver.id === row.driver.id
          ? {
              ...driver,
              operational_status: "Assigned",
              current_task_id: taskId,
              updated_at: nowIso,
            }
          : driver
      )
    );
    setAssignRow(null);
    pushNotice("info", `Assigning ${row.driver.name}…`);

    try {
      const res = await assignTaskDriver(taskId, row.driver.id);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || `Assignment failed (HTTP ${res.status}).`);
      }
      const confirmedName = data?.driver_name || row.driver.name;
      setTasks((prev) =>
        prev.map((entry) =>
          entry.id === taskId
            ? {
                ...entry,
                status: "Assigned",
                assigned_driver_id: row.driver.id,
                driver: confirmedName,
                assignedDriver: confirmedName,
                updatedAt: nowIso,
              }
            : entry
        )
      );
      pushNotice("success", `${confirmedName} assigned from Driver Status.`);
    } catch (err) {
      setTasks(previousTasks);
      setDrivers(previousDrivers);
      pushNotice("error", err?.message || "Assignment failed.");
      throw err;
    }
  };

  const applyRemovalToTask = useCallback((task, row, note, removedAt) => {
    if (!task || !row) return task;
    return {
      ...task,
      assigned_driver_id: null,
      assignedDriver: null,
      driver: null,
      reassignmentRequired: true,
      reassignmentReason: "driver_removed",
      reassignmentMessage: "DRIVER REMOVED — REASSIGN IMMEDIATELY",
      reassignmentRequiredAt: removedAt,
      reassignmentRemovedDriverId: row.driver.id,
      reassignmentRemovedDriverName: row.driver.name,
      reassignmentAdminNote: note || "",
      updatedAt: removedAt,
    };
  }, []);

  const handleRemoveFromShift = async (row, note, attachedTasks) => {
    const removedAt = new Date().toISOString();
    const attachedIds = new Set(attachedTasks.map((task) => task.id));
    const previousDrivers = drivers;
    const previousTasks = tasks;

    setDrivers((prev) =>
      prev.map((driver) =>
        driver.id === row.driver.id
          ? {
              ...driver,
              operational_status: "Offline",
              current_task_id: null,
              updated_at: removedAt,
            }
          : driver
      )
    );
    setTasks((prev) =>
      prev.map((task) => (attachedIds.has(task.id) ? applyRemovalToTask(task, row, note, removedAt) : task))
    );
    setRemoveRow(null);
    pushNotice("info", `Removing ${row.driver.name} from shift…`);

    try {
      const res = await removeDriverFromShift(row.driver.id, note);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || `Remove From Shift failed (HTTP ${res.status}).`);
      }

      const affectedIds = new Set((data.affectedTasks || []).map((task) => task.id));
      const confirmedAt = data.removedAt || removedAt;
      setDrivers((prev) =>
        prev.map((driver) => (driver.id === row.driver.id ? { ...driver, ...(data.driver || {}), operational_status: "Offline", current_task_id: null } : driver))
      );
      setTasks((prev) =>
        prev.map((task) => (affectedIds.has(task.id) ? applyRemovalToTask(task, row, note, confirmedAt) : task))
      );
      pushNotice(
        "success",
        `${row.driver.name} removed from shift. ${data.affectedTaskCount || 0} task${data.affectedTaskCount === 1 ? "" : "s"} marked for immediate reassignment.`
      );
    } catch (err) {
      setDrivers(previousDrivers);
      setTasks(previousTasks);
      pushNotice("error", err?.message || "Remove From Shift failed.");
      throw err;
    }
  };

  const handleLogoutDriver = async (row) => {
    const loggedOutAt = new Date().toISOString();
    const previousDrivers = drivers;
    setDrivers((prev) =>
      prev.map((driver) =>
        driver.id === row.driver.id
          ? {
              ...driver,
              operational_status: "Offline",
              current_status: "Offline",
              current_task_id: null,
              updated_at: loggedOutAt,
            }
          : driver
      )
    );
    pushNotice("info", `Logging out ${row.driver.name}…`);

    try {
      const res = await logoutDriver(row.driver.id);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || `Driver logout failed (HTTP ${res.status}).`);
      }
      setDrivers((prev) =>
        prev.map((driver) =>
          driver.id === row.driver.id
            ? {
                ...driver,
                ...(data || {}),
                operational_status: "Offline",
                current_status: "Offline",
                current_task_id: null,
                updated_at: data?.updated_at || loggedOutAt,
              }
            : driver
        )
      );
      pushNotice("success", `${row.driver.name} logged out and marked offline.`);
    } catch (err) {
      setDrivers(previousDrivers);
      pushNotice("error", err?.message || "Driver logout failed.");
    }
  };

  const fleetSummary = () =>
    driverRows
      .map(
        (r) =>
          `${r.driver.name} | shift: ${r.shiftState} | health: ${r.health} | ${r.stale ? "stale" : "fresh"} | ` +
          `${r.currentTask ? `task ${taskIdentity(r.currentTask).title}` : "no task"}`
      )
      .join("\n");

  // Explicit, user-triggered (never auto-polled) so the local model is not
  // hit on every 15 s fleet refresh.
  const handleAiSuggestions = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      setAiSuggestions(await getDispatchSuggestions(fleetSummary()));
    } catch (e) {
      setAiSuggestions([]);
      setAiError(e?.message || "AI suggestions are unavailable.");
    } finally {
      setAiLoading(false);
    }
  };

  const openChat = (row) => {
    setChatContext(
      `Driver ${row.driver.name}; shift: ${row.shiftState}; health: ${row.health}; ` +
        `${row.currentTask ? `current task ${taskIdentity(row.currentTask).title}` : "no current task"}`
    );
    setChatOpen(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box px={2} py={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Driver Status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fleet health and accountability board for drivers who need attention.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Last fleet sync {lastSync ? `${relativeTime(lastSync, nowTick)} • ${formatAbsoluteTime(lastSync)}` : "pending"}
              {refreshing ? " • updating…" : ""}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canUseAi && (
              <Button
                size="small"
                startIcon={aiLoading ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
                onClick={handleAiSuggestions}
                disabled={aiLoading || loading || drivers.length === 0}
              >
                {aiLoading ? "Thinking…" : "AI Suggestions"}
              </Button>
            )}
            <Button
              size="small"
              startIcon={loading || refreshing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
              onClick={() => load({ background: true })}
              disabled={loading || refreshing}
            >
              {loading || refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {canUseAi && aiError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAiError(null)}>
            {aiError}
          </Alert>
        )}

        {canUseAi && aiSuggestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              AI suggestions (advisory only)
            </Typography>
            {aiSuggestions.map((s, i) => (
              <AiSuggestionCard key={`${i}-${s.title}`} title={s.title} detail={s.detail} />
            ))}
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterCard label="On Shift" value={kpis.onShift.length} caption="Active workforce" selected={activeFilter === FILTERS.ON_SHIFT} onClick={() => setActiveFilter(FILTERS.ON_SHIFT)} color="info" />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterCard label="Available" value={kpis.available.length} caption="Ready for work" selected={activeFilter === FILTERS.AVAILABLE} onClick={() => setActiveFilter(FILTERS.AVAILABLE)} color="success" />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterCard label="In Transit" value={kpis.inTransit.length} caption="Currently moving" selected={activeFilter === FILTERS.IN_TRANSIT} onClick={() => setActiveFilter(FILTERS.IN_TRANSIT)} color="warning" />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterCard label="Offline" value={kpis.offline.length} caption="No active shift" selected={activeFilter === FILTERS.OFFLINE} onClick={() => setActiveFilter(FILTERS.OFFLINE)} color="default" />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterCard label="Stale / No Update" value={kpis.stale.length} caption="Needs accountability" selected={activeFilter === FILTERS.STALE} onClick={() => setActiveFilter(FILTERS.STALE)} color="warning" />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterCard label="On Task" value={kpis.onTask.length} caption="Task ownership" selected={activeFilter === FILTERS.ON_TASK} onClick={() => setActiveFilter(FILTERS.ON_TASK)} color="secondary" />
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ borderRadius: 3, border: (theme) => `1px solid ${theme.palette.divider}`, mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Fleet Health Board
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Driver accountability view. Use Courier Dashboard for exception resolution; quick assignment remains available here.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Last board evaluation {formatAbsoluteTime(new Date(nowTick).toISOString())}
                </Typography>
              </Box>
              {activeFilter !== FILTERS.ALL && (
                <Button size="small" onClick={() => setActiveFilter(FILTERS.ALL)}>
                  Clear Filter
                </Button>
              )}
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 1160 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "180px 120px 220px 200px 120px 120px 150px 220px",
                    gap: 2,
                    px: 1,
                    py: 1,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {["Driver", "Current status", "Current task", "Next destination", "Last seen", "Shift state", "Health flag", "Actions"].map((header) => (
                    <Typography key={header} variant="caption" sx={{ fontWeight: 800, color: "text.secondary" }}>
                      {header}
                    </Typography>
                  ))}
                </Box>

                {filteredRows.map((row) => {
                  const currentTaskIdentity = row.currentTask ? taskIdentity(row.currentTask) : null;
                  const currentTaskNote = isReassignmentRequired(row.currentTask) ? reassignmentMessage(row.currentTask) : null;
                  return (
                    <Box
                      key={row.driver.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "180px 120px 220px 200px 120px 120px 150px 220px",
                        gap: 2,
                        px: 1,
                        py: 1.5,
                        alignItems: "center",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor:
                          row.health === "Blocked" ? "error.50"
                          : row.health === "Override pending" ? "secondary.50"
                          : row.staleLevel === "critical" ? "warning.50"
                          : "transparent",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.driver.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.driver.email}</Typography>
                      </Box>
                      <Chip size="small" label={row.driver.operational_status || "Available"} color={badgeColor(row.driver.operational_status)} variant="outlined" sx={{ width: "fit-content", fontWeight: 700 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {currentTaskIdentity?.title || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.currentTask?.taskType ? String(row.currentTask.taskType).toUpperCase() : row.driver.current_task_id ? `${row.driver.current_task_id.slice(0, 8)}…` : "No current task"}
                        </Typography>
                        {currentTaskNote && (
                          <Typography variant="caption" color="error.main" display="block" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {currentTaskNote}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" title={nextDestination(row.currentTask)}>
                        {nextDestination(row.currentTask)}
                      </Typography>
                      <Box>
                        <Typography
                          variant="body2"
                          color={row.staleLevel === "critical" ? "error.main" : row.staleLevel === "warning" ? "warning.main" : "text.primary"}
                          sx={{ fontWeight: row.staleLevel === "healthy" ? 500 : 700 }}
                        >
                          {relativeTime(row.driver.updated_at, nowTick)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatAbsoluteTime(row.driver.updated_at)}
                        </Typography>
                      </Box>
                      <Typography variant="body2">{row.shiftState}</Typography>
                      <HealthChip value={row.health} />
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button size="small" onClick={() => openMap(row)} startIcon={<MapIcon fontSize="small" />}>
                          View on Map
                        </Button>
                        {canUseAi && (
                          <Button size="small" onClick={() => openChat(row)} startIcon={<ChatIcon fontSize="small" />}>
                            Ask AI
                          </Button>
                        )}
                        {row.currentTask && (
                          <>
                            <Button size="small" onClick={() => openRoute(row)} startIcon={<RouteIcon fontSize="small" />}>
                              Open Route
                            </Button>
                            <Button size="small" onClick={() => openTask(row)} startIcon={<OpenInNewIcon fontSize="small" />}>
                              Open Task
                            </Button>
                          </>
                        )}
                        {!row.currentTask && row.onShift && (
                          <Button size="small" onClick={() => setAssignRow(row)} startIcon={<PlaylistAddCheckIcon fontSize="small" />}>
                            Assign Work
                          </Button>
                        )}
                        {canLogoutDriver && row.onShift && (
                          <Button size="small" color="warning" onClick={() => handleLogoutDriver(row)}>
                            Log Out
                          </Button>
                        )}
                        {isAdmin && row.onShift && (
                          <Button size="small" color="error" onClick={() => setRemoveRow(row)}>
                            Remove From Shift
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <Card elevation={0} sx={{ borderRadius: 3, border: (theme) => `1px solid ${theme.palette.divider}`, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Drivers Needing Attention
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Surfaces stale drivers, blocked jobs, idle-too-long drivers, and missing updates.
                </Typography>
                {attentionRows.length === 0 ? (
                  <Alert severity="success">No drivers currently require escalation.</Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {attentionRows.map((row) => (
                      <Box key={row.driver.id} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2 }}>
                        <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.driver.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {row.health === "Blocked" && row.currentTask
                                ? isReassignmentRequired(row.currentTask)
                                  ? reassignmentMessage(row.currentTask)
                                  : `Blocked on ${taskIdentity(row.currentTask).title}`
                                : row.health === "Override pending" && row.currentTask
                                ? `Override pending on ${taskIdentity(row.currentTask).title}`
                                : row.idleTooLong
                                ? "Available too long without new work"
                                : !row.driver.updated_at
                                ? "No shift telemetry recorded"
                                : `Last seen ${relativeTime(row.driver.updated_at, nowTick)}`}
                            </Typography>
                          </Box>
                          <HealthChip value={row.health} />
                        </Box>
                        <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
                          <Button size="small" onClick={() => openMap(row)}>View on Map</Button>
                          {row.currentTask ? (
                            <>
                              <Button size="small" onClick={() => openTask(row)}>Open Task</Button>
                              <Button size="small" onClick={() => openRoute(row)}>Open Route</Button>
                            </>
                          ) : (
                            <Button size="small" onClick={() => setAssignRow(row)}>Assign Work</Button>
                          )}
                          {canLogoutDriver && row.onShift && (
                            <Button size="small" color="warning" onClick={() => handleLogoutDriver(row)}>
                              Log Out
                            </Button>
                          )}
                          {isAdmin && row.onShift && (
                            <Button size="small" color="error" onClick={() => setRemoveRow(row)}>
                              Remove From Shift
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card elevation={0} sx={{ borderRadius: 3, border: (theme) => `1px solid ${theme.palette.divider}`, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Available Drivers Pool
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  For quick staffing decisions and fast assignment. Full job control stays in Courier Dashboard / Task List.
                </Typography>
                {availablePool.length === 0 ? (
                  <Alert severity="info">No on-shift drivers are currently available.</Alert>
                ) : (
                  <Stack spacing={1.25}>
                    {availablePool.map((row) => (
                      <Box key={row.driver.id} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 1.5 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.driver.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last seen {relativeTime(row.driver.updated_at, nowTick)} • {formatAbsoluteTime(row.driver.updated_at)}
                            </Typography>
                          </Box>
                          <Box display="flex" gap={1}>
                            <HealthChip value={row.health} />
                            <Button size="small" onClick={() => setAssignRow(row)} disabled={!assignableTasks.length}>
                              Assign Work
                            </Button>
                            {canLogoutDriver && row.onShift && (
                              <Button size="small" color="warning" onClick={() => handleLogoutDriver(row)}>
                                Log Out
                              </Button>
                            )}
                            {isAdmin && row.onShift && (
                              <Button size="small" color="error" onClick={() => setRemoveRow(row)}>
                                Remove From Shift
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <QuickAssignDialog
          row={assignRow}
          tasks={assignableTasks}
          onClose={() => setAssignRow(null)}
          onAssign={handleQuickAssign}
        />
        <RemoveFromShiftDialog
          row={removeRow}
          tasks={tasks}
          onClose={() => setRemoveRow(null)}
          onConfirm={handleRemoveFromShift}
        />
        <Snackbar
          open={notice.open}
          autoHideDuration={4000}
          onClose={closeNotice}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert severity={notice.severity} variant="filled" onClose={closeNotice} sx={{ width: "100%" }}>
            {notice.message}
          </Alert>
        </Snackbar>
        {canUseAi && <AiChatBox open={chatOpen} onClose={() => setChatOpen(false)} context={chatContext} />}
      </Box>
    </DashboardLayout>
  );
}
